// Adverse-event data layer (F-AE-*). Ports the SEMANTICS of the legacy AE flow
// (execSubmitAE / buildAEEmailBody / openAEMarkSentPrompt / execMarkAESent /
// loadSafety, v20 lines ~3103-3291, 1869-1873) onto the 2.0 db envelope.
//
// The write chain, ported honestly:
//   1. rpc_report_adverse_event → the AE row is saved server-side (SECURITY
//      DEFINER: it stamps initials, reference, and the programme-routed PV inbox);
//   2. the report is composed into an IDENTIFIABLE pharmacovigilance email and
//      handed to the nurse's mail client via a `mailto:` (the view opens it) —
//      NO automatic send from the client, exactly the legacy fallback path;
//   3. the nurse confirms the send, and rpc_mark_ae_notified stamps the
//      reconciliation record (human-in-the-loop — the app never claims an email
//      left the building that a human didn't send).
//
// The recipient is DOMAIN-LOCKED to the @insightsiq.co PV inbox (AE_FORCE_RECIPIENT
// stays null): the body carries the patient's name/ID as the consent's safety
// clause authorises, so it may never be routed to an external address.
//
// SCOPE BYPASS (F-SCOPE-03): a serious AE is a safety item — the Today queue and
// the Safety register both show it regardless of the nurse's personal scope. The
// Safety register (v_safety_reconciliation) is programme-filtered only, never
// scope-filtered, so this bypass is structural here.

import { getClient, ok, fail, applyProgrammeFilter, fetchAll } from '../db/client.js';
import { VIEWS, RPCS, AE_EMAIL, AE_FORCE_RECIPIENT, SUSPECT_DEFAULT, AE_SEVERITY, AE_OUTCOME, RECONCILIATION_STATUS } from '../db/contract.js';
import { store, SLICES } from '../state/store.js';
import { logAudit, AUDIT_EVENT } from './audit.js';

// ─── Pure validation (unit-tested without a DB) ────────────────────────────

/**
 * Cross-check an AE form before it is sent (exact legacy execSubmitAE guards, in
 * order — the FIRST failure is returned so the nurse fixes one thing at a time).
 * Returns null when the form is internally consistent.
 *   1. reaction term, onset date and narrative are required;
 *   2. a SERIOUS event needs ≥1 seriousness criterion;
 *   3. a fatal / life-threatening SEVERITY must be reported as SERIOUS;
 *   4. ticking the "death" criterion requires Fatal severity to match.
 * @param {{reaction_term?: string, onset_date?: string, ae_description?: string, seriousness?: string, severity?: string, seriousness_criteria?: string[]}} form
 * @returns {string|null} the error message, or null when valid
 */
export function validateAE(form) {
  const f = form || {};
  const term = (f.reaction_term || '').trim();
  const onset = (f.onset_date || '').trim();
  const desc = (f.ae_description || '').trim();
  if (!term || !onset || !desc) return 'Reaction, onset date, and narrative are required';
  const ser = f.seriousness;
  const crits = f.seriousness_criteria || [];
  if (ser === 'serious' && crits.length === 0) return 'Serious AEs must have at least one criterion ticked';
  const sev = f.severity;
  if ((sev === 'fatal' || sev === 'life_threatening') && ser !== 'serious') {
    return 'A ' + (sev === 'fatal' ? 'fatal' : 'life-threatening') + ' severity must be reported as SERIOUS';
  }
  if (crits.includes('death') && sev !== 'fatal') {
    return '"Death" is ticked as a criterion — set Severity to Fatal to match';
  }
  return null;
}

const SEV_LABEL = Object.fromEntries(AE_SEVERITY.map((s) => [s.v, s.l]));
const OUT_LABEL = Object.fromEntries(AE_OUTCOME.map((o) => [o.v, o.l]));

/**
 * The PV email subject line (legacy: "[AE Report · Prog] SERIOUS · term · ref").
 * @param {{reaction_term?: string, seriousness?: string}} form
 * @param {{reference_number?: string, programme_name?: string}} [result] rpc result
 * @returns {string}
 */
export function aeEmailSubject(form, result) {
  const f = form || {};
  const r = result || {};
  const prog = r.programme_name ? ` · ${r.programme_name}` : '';
  const ser = f.seriousness === 'serious' ? 'SERIOUS · ' : '';
  return `[AE Report${prog}] ${ser}${f.reaction_term || ''} · ${r.reference_number || ''}`;
}

/**
 * Build the IDENTIFIABLE pharmacovigilance email body (exact legacy
 * buildAEEmailBody structure). Patient name/ID/DOB are included as the consent's
 * safety-reporting clause authorises and Kenyan AE law requires — the recipient
 * is domain-locked upstream. Pure: reporter + patient are passed in.
 * @param {any} result rpc_report_adverse_event result
 * @param {any} form the submitted form inputs
 * @param {any} patient the case row (name, national_id, dob, sex, phone, county…)
 * @param {{name?: string, role?: string, email?: string}} [reporter] signed-in staff
 * @returns {string}
 */
export function buildAEEmailBody(result, form, patient, reporter) {
  const r = result || {};
  const f = form || {};
  const p = patient || {};
  const rep = reporter || {};
  const sevLabel = SEV_LABEL[f.severity] || f.severity || '—';
  const outLabel = OUT_LABEL[f.outcome] || f.outcome || '—';
  const criteriaList = (f.seriousness_criteria || []).map((c) => '  • ' + String(c).replace(/_/g, ' ')).join('\n');
  let age = '';
  if (p.dob) {
    const b = new Date(p.dob);
    const n = new Date();
    let a = n.getFullYear() - b.getFullYear();
    const m = n.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && n.getDate() < b.getDate())) a--;
    if (a >= 0 && a < 130) age = String(a);
  }
  const facility = p.treating_facility || p.facility_name || r.facility || '—';
  const suspects = f.suspect_products && f.suspect_products.length ? f.suspect_products.join('\n') : SUSPECT_DEFAULT;
  return [
    'Dear Pharmacovigilance Team,',
    '',
    'A new adverse event has been reported through Tumaini PSP. This report contains IDENTIFIABLE patient information and is strictly confidential. Please action and acknowledge it, and undertake any onward regulatory reporting (e.g. to the Pharmacy and Poisons Board) required by law.',
    '',
    '== PATIENT ==',
    'Name: ' + (p.patient_name || '—'),
    'National ID / Passport: ' + (p.national_id || '—'),
    'Date of birth: ' + (p.dob || '—') + (age ? ' (age ' + age + ')' : ''),
    'Sex: ' + (p.sex || '—'),
    'Phone: ' + (p.patient_phone || '—'),
    'County: ' + (p.county || r.county || '—'),
    'Facility: ' + facility,
    'Case reference: ' + (r.reference_number || p.reference_number || '—'),
    'Initials: ' + (r.patient_initials || '—'),
    '',
    '== REACTION ==',
    'Reaction term: ' + (f.reaction_term || '—'),
    'Date of onset: ' + (f.onset_date || '—'),
    'Seriousness: ' + (f.seriousness === 'serious' ? 'SERIOUS' : 'Non-serious'),
    criteriaList ? 'Seriousness criteria:\n' + criteriaList : null,
    'Severity: ' + sevLabel,
    'Outcome: ' + outLabel,
    '',
    '== SUSPECT PRODUCT(S) ==',
    suspects,
    '',
    '== CO-MEDICATIONS ==',
    f.co_medications || '(none reported)',
    '',
    '== ACTION TAKEN ==',
    f.action_taken || '(see narrative)',
    '',
    '== NARRATIVE ==',
    f.ae_description || '(see internal record)',
    '',
    '== REPORTER ==',
    'Name: ' + (rep.name || 'Tumaini PSP staff'),
    'Role: ' + (rep.role || 'PSP staff'),
    'Email: ' + (rep.email || '—'),
    'Date of report: ' + new Date().toLocaleString(),
    '',
    'Internal AE record ID: ' + (r.ae_id || '—'),
    'Please reply with the official PV reference number once acknowledged.',
    '',
    '— ' + AE_EMAIL.signature_block,
  ]
    .filter((x) => x !== null)
    .join('\n');
}

/**
 * Compose the domain-locked mailto URL for the PV report. The recipient is
 * AE_FORCE_RECIPIENT (null in prod) OR the RPC-routed programme inbox OR the
 * default PV inbox — never anything the client picked. Pure.
 * @param {any} result rpc result (email_to / email_cc)
 * @param {string} subject
 * @param {string} body
 * @returns {{to: string, cc: string, url: string}}
 */
export function buildMailto(result, subject, body) {
  const r = result || {};
  const to = AE_FORCE_RECIPIENT || r.email_to || AE_EMAIL.to;
  const cc = AE_FORCE_RECIPIENT ? '' : r.email_cc || AE_EMAIL.cc;
  const url = `mailto:${to}?cc=${encodeURIComponent(cc)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return { to, cc, url };
}

/**
 * Reconciliation status pill (legacy renderSafety reconPill). A value the view
 * did not recognise reads as "Send email" — the honest not-yet-sent state.
 * @param {string} status v_safety_reconciliation.reconciliation_status
 * @returns {{cls: 'ok'|'warn'|'danger', label: string, title: string}}
 */
export function reconPill(status) {
  if (status === RECONCILIATION_STATUS.RECONCILED) {
    return { cls: 'ok', label: 'Reconciled', title: 'AE team has acknowledged' };
  }
  if (status === RECONCILIATION_STATUS.AWAITING_ACK) {
    return { cls: 'warn', label: 'Awaiting ack', title: 'Email sent, awaiting acknowledgment' };
  }
  return { cls: 'danger', label: 'Send email', title: 'Email not yet sent' };
}

// ─── Writers (envelope) ─────────────────────────────────────────────────────

/**
 * Report an adverse event: insert the AE via rpc_report_adverse_event and audit
 * it. Returns ok(result) — the RPC result carries {ae_id, reference_number,
 * patient_initials, email_to, email_cc, programme_name, facility, county} for the
 * PV email — or fail(error). The email itself is composed + sent by the caller
 * (mailto), then reconciled via markAENotified: this writer only lands the row.
 * @param {string} caseId
 * @param {any} form the validated form inputs (payload to the RPC)
 * @param {{client?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function reportAdverseEvent(caseId, form, deps) {
  const client = (deps && deps.client) || getClient();
  const { data: result, error } = await client.rpc(RPCS.REPORT_ADVERSE_EVENT, {
    p_case_id: caseId,
    p_payload: form,
  });
  if (error) return fail(error);
  const ser = form && form.seriousness === 'serious';
  await logAudit(
    AUDIT_EVENT.AE_REPORTED,
    `reported an adverse event — ${(form && form.reaction_term) || ''}${ser ? ' (SERIOUS)' : ''}`,
    caseId,
    { reaction: form && form.reaction_term, seriousness: form && form.seriousness },
    deps
  );
  return ok(result || {});
}

/**
 * Stamp the reconciliation record once the nurse confirms the PV email was sent
 * (legacy execMarkAESent → rpc_mark_ae_notified). p_team_reference is the PV
 * team's own reference if they already replied with one (optional).
 * @param {string} aeId
 * @param {{teamReference?: string|null, sentAt?: string}} [opts]
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function markAENotified(aeId, opts, deps) {
  const client = (deps && deps.client) || getClient();
  const { error } = await client.rpc(RPCS.MARK_AE_NOTIFIED, {
    p_ae_id: aeId,
    p_sent_at: (opts && opts.sentAt) || new Date().toISOString(),
    p_team_reference: (opts && opts.teamReference) || null,
  });
  if (error) return fail(error);
  return ok({ aeId });
}

/**
 * Load the Safety register (v_safety_reconciliation) into SLICES.SAFETY. Ported
 * from legacy loadSafety: programme-filtered (never scope-filtered — F-SCOPE-03),
 * newest first, fetched complete past the 1,000-row cap. Never throws — a failure
 * lands as loadError on the slice so the view shows "couldn't load — retry", never
 * a false empty register.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadSafety(deps) {
  const client = (deps && deps.client) || getClient();
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';
  const res = await fetchAll(() =>
    applyProgrammeFilter(
      client.from(VIEWS.SAFETY_RECONCILIATION).select('*', { count: 'exact' }),
      'programme_code',
      active,
      prog.byCode || {}
    ).order('report_date', { ascending: false })
  );
  // `loaded` marks that a load has RESOLVED (success or failure). Until it is
  // true the register must show a skeleton — an absent slice used to render
  // "No adverse events" while the first request was still in flight, which on
  // the one view that exists to keep serious AEs visible is a false all-clear.
  if (!res.ok) {
    store.set(SLICES.SAFETY, { rows: [], loadError: res.error, loaded: true });
    // No number after a failed reload — the same rule the Patients badge got
    // in R-13. Keeping the last good count lets the sidebar assert "Adverse
    // events 3" beside a register that says it could not load. countBadge()
    // hides anything that is not a positive number, so null is "unknown".
    const c = store.get(SLICES.COUNTS) || {};
    store.set(SLICES.COUNTS, Object.assign({}, c, { safety: null }));
    return fail(res.error);
  }
  const rows = res.data;
  const seriousOpen = rows.filter((r) => r.seriousness === 'serious').length;
  store.set(SLICES.SAFETY, { rows, loadError: null, loaded: true });
  // Serious-AE count feeds the sidebar 'safety' badge (legacy cnt-s).
  const counts = store.get(SLICES.COUNTS) || {};
  store.set(SLICES.COUNTS, Object.assign({}, counts, { safety: seriousOpen }));
  return ok(rows);
}
